import React from 'react'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { CompanyType } from 'proposals/components/SelectCompany'
import { Formik, Form } from 'formik'
import TextFormField from 'proposals/components/TextFormField'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'

const PrimaryAction = styled(Button)(({ theme: { palette } }) => ({
  color: palette.common.white,
  backgroundColor: palette.success.main,
  '&:hover': {
    backgroundColor: palette.success.dark
  }
}))

type AddBuyerDialogPropTypes = {
  onClose: () => void
  open: boolean
  addCompany: (company: CompanyType | null) => void
  initialBusinessId?: string
}

type CompanyAddFields = {
  name: string
  businessId: string
}

const AddCompanyDialog: React.FC<AddBuyerDialogPropTypes> = ({ onClose, open, addCompany, initialBusinessId = '' }) => {
  const { t } = useTranslation()

  const initialValues = {
    name: '',
    businessId: initialBusinessId
  }

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required(t('required-field')),
    businessId: yup
      .string()
      .trim()
      .required(t('required-field'))
      .matches(/^(\d{7}-\d|(\d\s*){6}-((\d\s*){4}))$/, t('value-is-not-a-business-id'))
  })

  const onSubmit = (values: CompanyAddFields): void => {
    const trimmedValues = { name: values.name.trim(), businessId: values.businessId.replace(/\s/g, '') }
    const company: CompanyType = {
      ...trimmedValues
    }

    addCompany(company)
    onClose()
  }

  const handleCancel = (): void => {
    addCompany(null)
    onClose()
  }

  return (
    <Dialog onClose={onClose} open={open} fullWidth>
      <DialogTitle>{initialBusinessId ? t('add-a-name-to-the-company') : t('add-company')}</DialogTitle>
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit}>
        {({ isValid, dirty }): React.ReactNode => (
          <>
            <Form>
              <DialogContent>
                <TextFormField label={t('company-name')} fullWidth name="name" required autoFocus autoComplete="off" />
                <TextFormField
                  sx={{ marginTop: 2, maxWidth: 250 }}
                  label={t('business-id')}
                  fullWidth
                  name="businessId"
                  required
                  autoComplete="off"
                />
              </DialogContent>
              <DialogActions>
                <Button color="grey" onClick={handleCancel}>
                  {t('cancel')}
                </Button>
                <PrimaryAction type="submit" variant="contained" color="primary" disabled={!dirty || !isValid}>
                  {t('add')}
                </PrimaryAction>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  )
}

export default AddCompanyDialog
